import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import {
  ArtifactStorageDisabledError,
  createArtifact,
  type ArtifactScope,
  type ArtifactType,
  type CreateArtifactBody,
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

interface CreateArtifactDialogProps {
  codebaseId: string;
  storageDisabled: boolean;
}

const TYPES: { value: ArtifactType; label: string }[] = [
  { value: 'issue', label: 'Issue' },
  { value: 'spec', label: 'Spec' },
  { value: 'adr', label: 'ADR' },
  { value: 'implementation_plan', label: 'Implementation Plan' },
  { value: 'note', label: 'Note' },
];

/**
 * Default scope for each type per the spec's Scope And Relationships rules:
 * ADR -> codebase; issue/spec/implementation_plan -> workstream; note -> codebase.
 */
function defaultScopeForType(t: ArtifactType): ArtifactScope {
  if (t === 'adr' || t === 'note') return 'codebase';
  return 'workstream';
}

/** Notes are the only type where the scope choice is meaningful (codebase vs workstream).
 *  Every other type has exactly one valid scope per the DB constraint. */
function scopeIsEditable(t: ArtifactType): boolean {
  return t === 'note';
}

export function CreateArtifactDialog({
  codebaseId,
  storageDisabled,
}: CreateArtifactDialogProps): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<ArtifactType>('spec');
  const [scope, setScope] = useState<ArtifactScope>(() => defaultScopeForType('spec'));
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setScope(defaultScopeForType(type));
  }, [type]);

  useEffect(() => {
    if (!open) {
      setType('spec');
      setScope('workstream');
      setTitle('');
      setError(null);
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: (body: CreateArtifactBody) => createArtifact(codebaseId, body),
    onSuccess: artifact => {
      void queryClient.invalidateQueries({ queryKey: ['artifacts'] });
      setOpen(false);
      navigate(`/artifacts/${encodeURIComponent(artifact.id)}`);
    },
    onError: err => {
      if (err instanceof ArtifactStorageDisabledError) {
        setError(err.message);
        return;
      }
      setError(err.message);
    },
  });

  function handleSubmit(): void {
    setError(null);
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    mutation.mutate({ type, scope, title: title.trim() });
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          disabled={storageDisabled}
          title={storageDisabled ? 'Artifact storage is disabled' : undefined}
          className="inline-flex items-center gap-1.5"
        >
          <Plus className="size-4" />
          New artifact
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create artifact</DialogTitle>
          <DialogDescription>Choose a type, give it a title, and start drafting.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 pt-2">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-text-secondary">Type</span>
            <select
              value={type}
              onChange={e => {
                setType(e.target.value as ArtifactType);
              }}
              className="rounded-md border border-border bg-surface px-2 py-1 text-sm"
            >
              {TYPES.map(t => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-text-secondary">Scope</span>
            <select
              value={scope}
              onChange={e => {
                setScope(e.target.value as ArtifactScope);
              }}
              disabled={!scopeIsEditable(type)}
              className="rounded-md border border-border bg-surface px-2 py-1 text-sm disabled:opacity-60"
            >
              <option value="codebase">codebase</option>
              <option value="workstream">workstream</option>
            </select>
            {!scopeIsEditable(type) && (
              <span className="text-xs text-text-secondary">
                Scope is fixed for this artifact type.
              </span>
            )}
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-text-secondary">Title</span>
            <input
              type="text"
              value={title}
              onChange={e => {
                setTitle(e.target.value);
              }}
              placeholder="e.g. Auth refactor"
              className="rounded-md border border-border bg-surface px-2 py-1 text-sm"
            />
          </label>
          {error && <div className="rounded-md bg-error/15 p-2 text-sm text-error">{error}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={(): void => {
                setOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={mutation.isPending || !title.trim()}>
              {mutation.isPending ? 'Creating…' : 'Create artifact'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
